import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { notifApi } from '../api/notifications.js';
import { getCollabSocket, disconnectCollab } from '../api/collab.js';
import { useAuth } from './AuthContext.jsx';

const NotificationContext = createContext(null);
// Long fallback poll only — the socket pushes new notifications instantly.
const POLL_MS = 5 * 60_000;

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [byCategory, setByCategory] = useState({});
  const [pulse, setPulse] = useState(false);
  const timerRef = useRef(null);

  const refresh = useCallback(async (params = {}) => {
    if (!user) return;
    try {
      const r = await notifApi.list({ limit: 30, ...params });
      setItems(r?.items || []);
      setUnread(r?.unread || 0);
      setByCategory(r?.by_category || {});
    } catch {
      // best effort — bell stays as-is
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setItems([]); setUnread(0); setByCategory({});
      disconnectCollab();
      return;
    }
    refresh();
    timerRef.current = setInterval(refresh, POLL_MS);

    // Live push from /collab — server sends 'notification:new' to user:<id>.
    const sock = getCollabSocket();
    const handler = (notif) => {
      setItems((prev) => [notif, ...prev].slice(0, 30));
      setUnread((c) => c + 1);
      if (notif?.category) {
        setByCategory((m) => ({ ...m, [notif.category]: (m[notif.category] || 0) + 1 }));
      }
      // Trigger pulse animation; auto-clears after 5s.
      setPulse(true);
      setTimeout(() => setPulse(false), 5000);
    };
    if (sock) sock.on('notification:new', handler);

    return () => {
      clearInterval(timerRef.current);
      if (sock) sock.off('notification:new', handler);
    };
  }, [user, refresh]);

  const markRead = useCallback(async (id) => {
    // Optimistic.
    setItems((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n));
    setUnread((c) => Math.max(0, c - 1));
    try { await notifApi.markRead(id); } catch { refresh(); }
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true, read_at: n.read_at || new Date().toISOString() })));
    setUnread(0);
    setByCategory({});
    try { await notifApi.markAllRead(); } catch { refresh(); }
  }, [refresh]);

  const removeOne = useCallback(async (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    try { await notifApi.remove(id); } catch { refresh(); }
  }, [refresh]);

  return (
    <NotificationContext.Provider value={{ items, unread, byCategory, pulse, refresh, markRead, markAllRead, removeOne }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
